import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Search,
  BookOpen,
  PlayCircle,
  FileText,
  Bookmark,
  Filter,
  ChevronDown,
  Star,
  Clock,
  Eye,
  TrendingUp,
  Book,
  Video,
  File,
  Newspaper,
  Layers,
  Award,
  CheckCircle,
  Clock as ClockIcon,
  Play,
  Menu,
  X
} from "lucide-react";
import { getMyResources } from "../../api/api";
import DigitalLibrary from "./DigitalLibrary";
import ResourceDetail from "./ResourceDetail";

// ============================================
// CONTINUE LEARNING COMPONENT
// ============================================
const ContinueLearning = React.memo(({ courses, onResume }) => {
  const defaultCourses = [
    // {
    //   id: 1,
    //   title: "Gut-Brain Axis: How Your Gut Affects Your Mood",
    //   subtitle: "Gut Health & Microbiome Essentials • Module 3",
    //   progress: 72,
    //   image: "https://images.unsplash.com/photo-1532187643603-ba119ca4109e?w=300",
    // },
    // {
    //   id: 2,
    //   title: "Pre & Post Workout Nutrition Strategies",
    //   subtitle: "Sports Nutrition Foundations • Module 2",
    //   progress: 40,
    //   image: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=300",
    // },
  ];

  const data = courses || defaultCourses;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-[#1f2937] flex items-center gap-2">
          <PlayCircle size={20} className="text-green-600" />
          Continue Learning
        </h2>
      </div>

      <div className="space-y-4">
        {data.map((course) => (
          <div
            key={course.id}
            className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-4 hover:shadow-md transition-shadow duration-200"
          >
            <img
              src={course.image || course.course_image || "https://images.unsplash.com/photo-1532187643603-ba119ca4109e?w=300"}
              alt={course.title || course.course_title}
              className="w-[52px] h-[52px] rounded-lg object-cover flex-shrink-0"
              loading="lazy"
            />

            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 truncate">
                {course.title || course.course_title}
              </h3>
              <p className="text-xs text-gray-400 mt-0.5 truncate">
                {course.subtitle || course.course_slug || "Course"}
              </p>

              <div className="mt-2 flex items-center gap-3">
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-600 rounded-full transition-all duration-500"
                    style={{ width: `${course.progress || course.progress_percentage || 0}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-green-600 min-w-[35px] text-right">
                  {course.progress || course.progress_percentage || 0}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

// ============================================
// RESOURCE CARD COMPONENT
// ============================================
const ResourceCard = React.memo(({
  image,
  title,
  type,
  duration,
  views,
  progress,
  status,
  action,
  rating,
  onClick,
  onBookmark,
  thumbnail,
  file_url,
  author,
  course,
  module,
  badge,
  is_featured,
  description,
  created_at,
  tags,
  sections,
  transcript,
  objectives,
  prerequisites
}) => {
  const getStatusColor = useCallback((status) => {
    const colors = {
      "Completed": "bg-green-500",
      "In Progress": "bg-orange-400",
      "Not Started": "bg-gray-300"
    };
    return colors[status] || "bg-gray-300";
  }, []);

  const getStatusTextColor = useCallback((status) => {
    const colors = {
      "Completed": "text-green-600",
      "In Progress": "text-orange-600",
      "Not Started": "text-gray-500"
    };
    return colors[status] || "text-gray-500";
  }, []);

  const getTypeIcon = useCallback((type) => {
    const icons = {
      "Video": <Video size={14} />,
      "Guide": <Book size={14} />,
      "Article": <FileText size={14} />,
      "Case Study": <Newspaper size={14} />
    };
    return icons[type] || <File size={14} />;
  }, []);

  const [isBookmarked, setIsBookmarked] = useState(false);

  const handleBookmark = useCallback((e) => {
    e.stopPropagation();
    setIsBookmarked(!isBookmarked);
    if (onBookmark) onBookmark(title, !isBookmarked);
  }, [isBookmarked, onBookmark, title]);

  const displayImage = image || thumbnail || "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=800";

  return (
    <div
      className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer"
      onClick={onClick}
    >
      <div className="relative">
        <img
          src={displayImage}
          alt={title}
          className="h-48 w-full object-cover"
          loading="lazy"
        />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
            {getTypeIcon(type || "Video")}
            {type || "Video"}
          </span>
          {badge && badge.text && (
            <span className={`bg-${badge.color || 'gold'}-500/90 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1`}>
              {badge.text}
            </span>
          )}
        </div>
        {progress === 100 && (
          <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
            <CheckCircle size={14} />
            Completed
          </div>
        )}
        {is_featured && (
          <div className="absolute bottom-3 left-3 bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-medium">
            Featured
          </div>
        )}
      </div>

      <div className="p-5">
        {course && (
          <p className="text-xs text-green-600 font-medium mb-1 truncate">
            {course.title}
          </p>
        )}
        {module && (
          <p className="text-xs text-gray-500 mb-1 truncate">
            {module.title}
          </p>
        )}
        <h3 className="font-bold text-lg text-gray-900 line-clamp-2">
          {title}
        </h3>

        <div className="flex items-center gap-4 text-sm text-gray-500 mt-3">
          {duration && (
            <span className="flex items-center gap-1">
              <Clock size={14} />
              {duration}
            </span>
          )}
          {views !== undefined && views !== null && (
            <span className="flex items-center gap-1">
              <Eye size={14} />
              {typeof views === 'number' ? views.toLocaleString() : views}
            </span>
          )}
          {/* {rating && (
            <span className="flex items-center gap-1 text-orange-500">
              <Star size={14} fill="currentColor" />
              {typeof rating === 'number' ? rating.toFixed(1) : rating}
            </span>
          )} */}
          {author && (
            <span className="flex items-center gap-1 text-gray-400 truncate">
              By {author.name}
            </span>
          )}
        </div>

        {progress !== undefined && progress !== null && (
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span className={getStatusTextColor(status || "Not Started")}>
                {status || "Not Started"}
              </span>
              <span className="text-gray-600">{progress}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${getStatusColor(status || "Not Started")}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-5">
          <button
            className="bg-green-50 text-green-700 px-4 py-2 rounded-xl text-sm font-medium hover:bg-green-100 transition"
            onClick={(e) => {
              e.stopPropagation();
              if (onClick) onClick();
            }}
          >
            {action || (progress > 0 ? "Continue" : "Start Learning")}
          </button>

          <div className="flex items-center gap-2">
            <button
              className="p-2 hover:bg-gray-100 rounded-lg transition"
              onClick={handleBookmark}
            >
              {/* <Bookmark 
                size={18} 
                className={isBookmarked ? "text-green-600 fill-green-600" : "text-gray-400"} 
              /> */}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

// ============================================
// RIGHT SIDEBAR COMPONENT
// ============================================
const RightSidebar = React.memo(({
  learningStats,
  recommendedResources,
  topics,
  onViewAll,
  onTopicClick,
  onCertificateClick
}) => {
  const defaultStats = {
    courseCompletion: 64,
    moduleCompletion: 78,
    resourcesViewed: 16,
    totalResources: 28
  };

  const defaultTopics = [
    "Macronutrients",
    "Gut Health",
    "Meal Planning",
    "Weight Management",
    "Sports Nutrition",
    "Vitamins",
    "Hydration",
    "Metabolism"
  ];

  const defaultRecommended = [
    { id: 1, title: "Anti-Inflammatory Foods", duration: "5 min read" },
    { id: 2, title: "Meal Prep Masterclass", duration: "8 min read" },
    { id: 3, title: "Microbiome Research 2024", duration: "12 min read" },
    { id: 4, title: "Protein Timing Guide", duration: "6 min read" }
  ];

  const stats = learningStats || defaultStats;
  const recommendations = recommendedResources || defaultRecommended;
  const tags = topics || defaultTopics;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp size={18} className="text-green-600" />
            My Learning Progress
          </h3>
        </div>

        <div className="mt-5 space-y-5">
          <ProgressItem
            label="Course Completion"
            value={stats.courseCompletion || 0}
            color="bg-green-500"
          />
          <ProgressItem
            label="Module Completion"
            value={stats.moduleCompletion || 0}
            color="bg-blue-500"
          />
          <ProgressItem
            label="Resources Viewed"
            value={stats.resourcesViewed && stats.totalResources ? Math.round((stats.resourcesViewed / stats.totalResources) * 100) : 0}
            color="bg-orange-400"
            subtitle={`${stats.resourcesViewed || 0} / ${stats.totalResources || 0}`}
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            Recommended For You
          </h3>
        </div>

        <div className="space-y-4 mt-5">
          {recommendations.map((item) => (
            <RecommendedItem
              key={item.id}
              title={item.title}
              duration={item.duration}
              onClick={() => onViewAll?.(item.id)}
            />
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
          <Layers size={18} className="text-green-600" />
          Related Topics
        </h3>

        <div className="flex flex-wrap gap-2">
          {tags.map((tag, index) => (
            <span
              key={index}
              className="px-3 py-2 rounded-full bg-green-50 text-green-700 text-sm font-medium hover:bg-green-100 transition cursor-pointer"
              onClick={() => onTopicClick?.(tag)}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
});

const ProgressItem = React.memo(({ label, value, color, subtitle }) => (
  <div>
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">{label}</span>
      <span className="font-semibold text-gray-900">{subtitle || `${value}%`}</span>
    </div>
    <div className="h-2 bg-gray-100 rounded-full mt-2 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${color}`}
        style={{ width: `${Math.min(value, 100)}%` }}
      />
    </div>
  </div>
));

const RecommendedItem = React.memo(({ title, duration, onClick }) => (
  <div
    className="border-b border-gray-100 last:border-0 pb-3 last:pb-0 hover:bg-gray-50 p-2 rounded-lg transition cursor-pointer"
    onClick={onClick}
  >
    <h4 className="font-medium text-gray-900">{title}</h4>
    <div className="flex items-center gap-3 mt-1">
      <span className="text-sm text-gray-500 flex items-center gap-1">
        <Clock size={12} />
        {duration}
      </span>
    </div>
  </div>
));

const StatsCard = React.memo(({ icon: Icon, value, label, color }) => {
  const colorMap = {
    green: "bg-green-50 text-green-600",
    blue: "bg-blue-50 text-blue-600",
    orange: "bg-orange-50 text-orange-500"
  };

  return (
    <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
      <div className={`w-10 h-10 rounded-xl ${colorMap[color]} flex items-center justify-center`}>
        <Icon size={18} />
      </div>
      <h2 className="text-2xl font-bold mt-3">{value}</h2>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  );
});

// ============================================
// FILTER DROPDOWN COMPONENT
// ============================================
const FilterDropdown = React.memo(({ value, onChange, categories }) => {
  const defaultCategories = [
    "All",
    "Nutrition",
    "Health",
    "Fitness",
    "Wellness",
    "Diet",
    "Exercise",
    "Mental Health",
    "General"
  ];

  const options = categories || defaultCategories;

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="form-select px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none pr-10"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
        backgroundPosition: 'right 0.75rem center',
        backgroundSize: '1.5em 1.5em',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {options.map((category) => (
        <option key={category} value={category}>
          {category}
        </option>
      ))}
    </select>
  );
});

// ============================================
// MAIN LEARNING RESOURCES COMPONENT
// ============================================
const LearningResources = ({ onNavigateToDigitalLibrary }) => {
  // State
  const [activeFilter, setActiveFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [resources, setResources] = useState([]);
  const [continueCourses, setContinueCourses] = useState([]);
  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [stats, setStats] = useState({
    totalResources: 0,
    coursesEnrolled: 0,
    resourcesCompleted: 0,
    savedResources: 0,
    courseCompletion: 0,
    moduleCompletion: 0,
    resourcesViewed: 0
  });
  const [categories, setCategories] = useState(["All"]);
  const [authors, setAuthors] = useState([]);
  const [showDigitalLibrary, setShowDigitalLibrary] = useState(false);
  
  // NEW: State for Resource Details
  const [selectedResource, setSelectedResource] = useState(null);
  const [isResourceDetailsOpen, setIsResourceDetailsOpen] = useState(false);

  // Constants
  const defaultContinueCourses = [
    // {
    //   id: 1,
    //   title: "Gut-Brain Axis: How Your Gut Affects Your Mood",
    //   subtitle: "Gut Health & Microbiome Essentials • Module 3",
    //   progress: 72,
    //   image: "https://images.unsplash.com/photo-1532187643603-ba119ca4109e?w=300",
    // },
    // {
    //   id: 2,
    //   title: "Pre & Post Workout Nutrition Strategies",
    //   subtitle: "Sports Nutrition Foundations • Module 2",
    //   progress: 40,
    //   image: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=300",
    // },
  ];

  // Add this callback in LearningResources component
const handleProgressUpdate = useCallback((videoId, progressData) => {
  

  setResources(prevResources => 
    prevResources.map(resource => {

      if ((resource.video_id === videoId || resource.id === videoId) && resource.type === 'Video') {
        return {
          ...resource,
          progress: progressData.progress_percentage,
          watch_time_seconds: progressData.watch_time_seconds,
          resume_position: progressData.resume_position,
          status: progressData.progress_percentage === 100 ? 'Completed' : 'In Progress'
        };
      }
      return resource;
    })
  );
  
  setContinueCourses(prevCourses => 
    prevCourses.map(course => {
      if (course.id === videoId) {
        return {
          ...course,
          progress: progressData.progress_percentage
        };
      }
      return course;
    })
  );
}, []);


  useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);
        const response = await getMyResources();

        if (response.success && response.data) {
          const data = response.data;
          setApiData(data);

          const allResources = [];
          data.grouped?.forEach(group => {
            group.resources?.forEach(resource => {
              allResources.push({
                ...resource,
                groupName: group.group_name,
                type: resource.type_label || resource.resource_type || "Video",
                duration: resource.duration_formatted || "00:00",
                views: resource.views || 0,
                progress: resource.user_progress?.progress || 0,
                status: resource.user_progress?.status || "Not Started",
                rating: resource.rating || "0.0",
                image: resource.thumbnail || resource.cover_image,
                action: resource.user_progress?.progress > 0 ? "Continue" : "Start Learning",
             
                description: resource.description || '',
                created_at: resource.created_at || null,
                tags: resource.tags || [],
                sections: resource.sections || [],
                transcript: resource.transcript || '',
                objectives: resource.objectives || [],
                prerequisites: resource.prerequisites || [],
                file_url: resource.file_url || null,
                author: resource.author || null,
                course: resource.course || null,
                module: resource.module || null
              });
            });
          });

          setResources(allResources);

          const progressCourses = data.my_progress?.courses || [];
          const continueData = progressCourses.map(course => ({
            id: course.course_id,
            title: course.course_title,
            subtitle: course.course_slug || "Course",
            progress: course.progress_percentage || 0,
            image: course.course_image || "https://images.unsplash.com/photo-1532187643603-ba119ca4109e?w=300",
            status: course.status
          }));
          setContinueCourses(continueData);

          const recommendedData = progressCourses.map(course => ({
            id: course.course_id,
            title: course.course_title,
            progress: course.progress_percentage || 0,
            duration: `${course.progress_percentage || 0}% complete`
          }));
          setRecommendedCourses(recommendedData);

          const statsData = data.stats || {};
          const progressSummary = data.my_progress?.summary || {};
          setStats({
            totalResources: statsData.total_resources || 0,
            coursesEnrolled: progressSummary.total_courses || 0,
            resourcesCompleted: progressSummary.completed_courses || 0,
            savedResources: 0,
            courseCompletion: progressSummary.average_progress || 0,
            moduleCompletion: 0,
            resourcesViewed: 0
          });

          const cats = data.filters?.categories?.map(c => c.category) || [];
          setCategories(["All", ...cats]);
          setAuthors(data.filters?.authors || []);
        } else {
          setError("Failed to load resources");
        }
      } catch (err) {
        console.error("Error fetching resources:", err);
        setError(err.message || "Failed to load resources");
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  // Memoized filtered resources - MUST be defined before callbacks that use it
  const filteredResources = useMemo(() => {
    let filtered = [...resources];

    if (activeFilter !== "All") {
      filtered = filtered.filter(r =>
        r.category === activeFilter ||
        r.type === activeFilter ||
        r.resource_type === activeFilter
      );
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.title?.toLowerCase().includes(query) ||
        r.category?.toLowerCase().includes(query) ||
        r.groupName?.toLowerCase().includes(query) ||
        r.type?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [resources, activeFilter, searchQuery]);

  // Callbacks - MUST be defined AFTER filteredResources
  const handleFilterChange = useCallback((filter) => {
    setActiveFilter(filter);
  }, []);

  // Handle resource click to open details
  const handleResourceClick = useCallback((resource) => {
    setSelectedResource(resource);
    setIsResourceDetailsOpen(true);
  }, []);

  // Close resource details
  const handleCloseResourceDetails = useCallback(() => {
    setIsResourceDetailsOpen(false);
    setSelectedResource(null);
  }, []);

  // Navigate to next resource
  const handleNextResource = useCallback(() => {
    const currentIndex = filteredResources.findIndex(r => r.id === selectedResource?.id);
    if (currentIndex < filteredResources.length - 1) {
      setSelectedResource(filteredResources[currentIndex + 1]);
    }
  }, [selectedResource, filteredResources]);

  // Navigate to previous resource
  const handlePreviousResource = useCallback(() => {
    const currentIndex = filteredResources.findIndex(r => r.id === selectedResource?.id);
    if (currentIndex > 0) {
      setSelectedResource(filteredResources[currentIndex - 1]);
    }
  }, [selectedResource, filteredResources]);

  const handleBookmark = useCallback((title, isBookmarked) => {
  }, []);

  const handleTopicClick = useCallback((topic) => {
    setSearchQuery(topic);
  }, []);

  const handleViewAll = useCallback((type) => {
  }, []);

  const handleCertificateClick = useCallback(() => {
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchQuery("");
    setActiveFilter("All");
  }, []);

  const handleResumeCourse = useCallback((courseId) => {

  }, []);

  const filterCategories = useMemo(() => {
    const cats = new Set();
    resources.forEach(r => {
      if (r.category) cats.add(r.category);
      if (r.type) cats.add(r.type);
      if (r.resource_type) cats.add(r.resource_type);
    });
    return ["All", ...Array.from(cats)];
  }, [resources]);

  const statItems = [
    { icon: BookOpen, value: stats.totalResources, label: "Resources Available", color: "green" },
    { icon: PlayCircle, value: stats.coursesEnrolled, label: "Courses Enrolled", color: "blue" },
    { icon: FileText, value: stats.resourcesCompleted, label: "Resources Completed", color: "green" },
    { icon: Bookmark, value: stats.savedResources, label: "Saved Resources", color: "orange" },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading resources...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
        <div className="text-6xl mb-4">⚠️</div>
        <h3 className="text-xl font-semibold text-gray-700">Failed to load resources</h3>
        <p className="text-gray-500 mt-2">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        {/* LEFT CONTENT */}
        <div>
          {/* TITLE */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#23352d]">
                Learning Resources
              </h1>
              <p className="text-gray-500 mt-1 text-sm md:text-base">
                Explore resources attached to your enrolled courses and health programs.
              </p>
            </div>

            <div className="flex gap-3 w-full md:w-auto">
              <button
                onClick={() => {
                  if (onNavigateToDigitalLibrary) {
                    onNavigateToDigitalLibrary();
                  } else {
                    setShowDigitalLibrary(true);
                  }
                }}
                className="bg-green-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-green-700 transition flex-1 md:flex-none"
              >
                Browse Library
              </button>
            </div>
          </div>

          {/* STATS */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            {statItems.map((item, i) => (
              <StatsCard key={i} {...item} />
            ))}
          </div>

          {/* SEARCH + FILTERS */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mt-6">
            <div className="relative w-full md:w-[250px]">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                placeholder="Search learning resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-11 pl-11 pr-4 border border-gray-200 rounded-xl bg-white outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-sm"
              />
            </div>

            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <FilterDropdown
                value={activeFilter}
                onChange={handleFilterChange}
                categories={filterCategories}
              />
            </div>
          </div>

          {/* SECTION */}
          <div className="mt-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div>
              <h2 className="font-bold text-lg text-gray-900">
                {activeFilter !== "All" ? activeFilter : "All Resources"}
              </h2>
            </div>
          </div>

          {/* CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            {filteredResources.map((item) => (
              <ResourceCard
                key={item.id}
                {...item}
                onClick={() => handleResourceClick(item)}
                onBookmark={handleBookmark}
              />
            ))}
          </div>

          {/* No Results */}
          {filteredResources.length === 0 && (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-200 mt-6">
              <div className="flex justify-center mb-4">
                <Search className="w-16 h-16 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-700">No resources found</h3>
              <p className="text-gray-500 mt-2">
                Try adjusting your filters or search query
              </p>
              <button
                onClick={handleClearFilters}
                className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Clear Filters
              </button>
            </div>
          )}

          {/* CONTINUE LEARNING */}
          <ContinueLearning
            courses={continueCourses.length > 0 ? continueCourses : defaultContinueCourses}
            onResume={handleResumeCourse}
          />
        </div>

        {/* RIGHT SIDEBAR */}
        <div className="hidden lg:block">
          <RightSidebar
            learningStats={stats}
            recommendedResources={recommendedCourses}
            topics={[]}
            onViewAll={handleViewAll}
            onTopicClick={handleTopicClick}
            onCertificateClick={handleCertificateClick}
          />
        </div>
      </div>

      {/* Mobile Sidebar */}
      <div className="lg:hidden mt-8">
        <RightSidebar
          learningStats={stats}
          recommendedResources={recommendedCourses}
          topics={[]}
          onViewAll={handleViewAll}
          onTopicClick={handleTopicClick}
          onCertificateClick={handleCertificateClick}
        />
      </div>
{isResourceDetailsOpen && selectedResource && (
  <ResourceDetail
    resource={selectedResource}
    onClose={handleCloseResourceDetails}
    onNext={handleNextResource}
    onPrevious={handlePreviousResource}
    hasNext={filteredResources.findIndex(r => r.id === selectedResource.id) < filteredResources.length - 1}
    hasPrevious={filteredResources.findIndex(r => r.id === selectedResource.id) > 0}
    onProgressUpdate={handleProgressUpdate} // Add this
  />
)}
    </div>
  );
};

export default LearningResources;